import { FormEvent, useState, useEffect } from "react";
import { Col, Form, FormGroup, Input, Label } from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Btn, H2, P } from "../AbstractElements";
import { backendbaseUrl } from "../Utils/baseUrl";
import axios from "axios";
import { getAccessToken, refreshAccessToken } from "../Utils/auth";
import { isTokenValid } from "../Utils/auth";
import { encryptData } from "../Tools/Cipher/EncryptData";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { motion } from "framer-motion";

export default function SignInForm() {
  const navigate = useNavigate();

  useEffect(() => {
    const token = getAccessToken();
    if (token && isTokenValid(token)) {
      navigate("/dashboard/default");
    } else if (token) {
      const refreshToken = async () => {
        const newToken = await refreshAccessToken();
        if (newToken) {
          navigate("/dashboard/default");
        }
      };
      refreshToken();
    }
  }, [navigate]);

  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const toggle = () => setPasswordVisible(!isPasswordVisible);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${backendbaseUrl}/users/login/`, {
        email,
        password,
      });
      if (response.status === 200) {
        localStorage.setItem("accessToken", response.data.token.access);
        localStorage.setItem("refreshToken", response.data.token.refresh);
        toast.success("Login Success!");

        if (response.data.user.user_type === "super_admin") {
          const encryptedData: any = encryptData(
            response.data.user.permissions
          );
          localStorage.setItem("resourceList", encryptedData);
          setTimeout(() => {
            window.location.reload();
          }, 1000);
          navigate(`/dashboard/default`);
        } else {
          navigate("/organization-apps");
        }
      }
    } catch (error: any) {
      if (error.response) {
        if (error.response.status === 401) {
          toast.error("Invalid Username or Password");
        } else {
          toast.error(
            error.response.data.message || "An unknown error occurred"
          );
        }
      } else {
        toast.error("Network error or server is down");
      }
    }
  };

  return (
    <div

    >
      {/* Background glows */}
      <div

      />
      <div
        style={{
          position: "absolute",
          width: "400px",
          height: "400px",
          bottom: "-100px",
          left: "-100px",
          borderRadius: "50%",
          background: "rgba(0,157,181,0.15)",
          filter: "blur(120px)",
          zIndex: 0,
        }}
      />

      {/* Logo */}
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        style={{ position: "relative", zIndex: 1, marginBottom: "25px" }}
      >
        <Link to={`${process.env.PUBLIC_URL}/dashboard/default`} className="logo" style={{ textDecoration: "none" }}>
          <h1
            style={{
              textAlign: "center",
              fontWeight: "bold",
              fontSize: "34px",
              letterSpacing: "1px",
              color: "#00c4e4",
              textShadow: "0 0 12px rgba(0,157,181,0.6)",
            }}
          >
            ACERS
          </h1>
        </Link>
      </motion.div>

      {/* Login Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        style={{
          width: "400px",
          background: "rgba(255, 255, 255, 0.05)",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          borderRadius: "18px",
          backdropFilter: "blur(12px)",
          boxShadow: "0 8px 30px rgba(0,0,0,0.4)",
          padding: "35px 40px",
          zIndex: 1,
        }}
      >
        <Form className="theme-form" onSubmit={handleSubmit}>
          <H2
            style={{
              textAlign: "center",
              fontWeight: 600,
              fontSize: "22px",
              marginBottom: "25px",
              color: "#00c4e4",
              textShadow: "0 0 8px rgba(0,157,181,0.4)",
            }}
          >
            Sign In to Your Account
          </H2>

          {/* Email */}
          <FormGroup style={{ marginTop: "20px" }}>
            <Col>
              <Label style={{ fontWeight: 500, fontSize: "14px", color: "#bde9f2" }}>
                Email Address
              </Label>
            </Col>
            <Input
              type="email"
              placeholder="Enter your email address"
              defaultValue={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{
                fontSize: "14px",
                padding: "10px 12px",
                borderRadius: "8px",
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.15)",
                color: "#eaf9fb",
              }}
            />
          </FormGroup>

          {/* Password */}
          <FormGroup>
            <Col>
              <Label style={{ fontWeight: 500, fontSize: "14px", color: "#bde9f2" }}>
                Password
              </Label>
            </Col>
            <div style={{ position: "relative" }}>
              <Input
                type={isPasswordVisible ? "text" : "password"}
                placeholder="Enter your password"
                defaultValue={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{
                  fontSize: "14px",
                  padding: "10px 40px 10px 12px",
                  borderRadius: "8px",
                  background: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.15)",
                  color: "#eaf9fb",
                }}
              />
              <div
                onClick={toggle}
                style={{
                  cursor: "pointer",
                  position: "absolute",
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#a5cfd7",
                }}
              >
                {isPasswordVisible ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
              </div>
            </div>
          </FormGroup>

          {/* Remember + Forgot */}
          <div className="mb-0 form-group" style={{ marginTop: "15px" }}>
            <div className="checkbox p-0" style={{ display: "flex", justifyContent: "space-between" }}>
              <div>
                <Input id="checkbox1" type="checkbox" />
                <Label
                  className="text-muted"
                  htmlFor="checkbox1"
                  style={{ fontSize: "13px", color: "#bde9f2", marginLeft: "5px" }}
                >
                  Remember Password
                </Label>
              </div>
              <Link
                className="link"
                to={`${process.env.PUBLIC_URL}/auth/forget_password`}
                style={{ fontSize: "13px", color: "#00c4e4" }}
              >
                Forgot Password?
              </Link>
            </div>

            {/* Sign In Button */}
            <div className="text-center mt-4">
              <Btn
                color="primary"
                style={{
                  width: "100%",
                  fontWeight: 600,
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                  padding: "10px 0",
                  borderRadius: "10px",
                  background: "linear-gradient(90deg, #007f94 0%, #009db5 100%)",
                  border: "none",
                  boxShadow: "0 0 10px rgba(0,157,181,0.6)",
                  transition: "all 0.3s ease",
                  marginLeft: "5px"
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.boxShadow = "0 0 16px rgba(0,200,255,0.8)")
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.boxShadow = "0 0 10px rgba(0,157,181,0.6)")
                }
              >
                Sign In
              </Btn>
            </div>
          </div>

          {/* Trial */}
          <P
            className="mt-4 mb-0 text-center"
            style={{ fontSize: "13px", color: "#bde9f2" }}
          >
            Want a trial session?{" "}
            <Link
              className="ms-2"
              to={`${process.env.PUBLIC_URL}/trail`}
              style={{
                color: "#00c4e4",
                fontWeight: 500,
                textDecoration: "underline",
              }}
            >
              Click Here
            </Link>
          </P>
        </Form>
      </motion.div>
    </div>
  );
}
